import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingCart, Clock, CreditCard, Monitor, CheckCircle, Zap } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOHead from "@/components/SEOHead";
import { useProductViewed } from "@/utils/trackingHooks";
import HeroVisualCard from "@/components/HeroVisualCard";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function OnlineOrderingPage() {
  useProductViewed("online_ordering");
  return (
    <div>
      <SEOHead title="Online Ordering System for Restaurants" description="Boost your takeaway sales with our online ordering systems for cafes, retail shops and restaurants. Our easy-to-use platform enhances customer experiences." path="/online-ordering" service={{ name: "Online Ordering", description: "Online ordering and checkout for UK hospitality, takeaway and retail — EPOS-synced so every order lands cleanly in the kitchen or warehouse.", serviceType: "Online Ordering Platform" }} />
      <section className="hero-gradient py-16 md:py-24" data-testid="oo-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Online Ordering</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                Let customers order and pay <span className="text-brand-aqua">online</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
                A straightforward online ordering solution for restaurants, takeaways, cafes, retail shops and service businesses. Customers order, pay securely, and you get clear confirmations — without managing everything manually.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/contact?product=Online%20Ordering&category=online-ordering&source=online-ordering-page" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="oo-cta-quote">Get online ordering pricing <ArrowRight size={16} /></Link>
              </motion.div>
            </motion.div>
            <HeroVisualCard
              icon={ShoppingCart}
              accentIcons={[Zap, CreditCard, Monitor]}
              accentLabels={["Real-time orders", "Secure checkout", "EPOS synced"]}
              testId="oo-hero-visual"
            />
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShoppingCart, label: "Easy Online Menus" },
            { icon: Zap, label: "Real-Time Orders" },
            { icon: CreditCard, label: "Secure Payments" },
            { icon: Monitor, label: "EPOS Integration" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light">
              <item.icon size={20} className="text-brand-aqua flex-shrink-0" />
              <span className="text-sm font-semibold text-brand-black">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-gray-50" data-testid="oo-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-6">Increase revenue, reduce admin</h2>
              <p className="text-brand-grey leading-relaxed mb-6">Your online menu or product list is easy to update, with clear options, pricing and availability. Orders come through in real time and can be sent directly to your EPOS system or email.</p>
              <div className="space-y-3">
                {["Easy-to-update menus and product listings", "Real-time order notifications", "Secure card payments at checkout", "Connects to your existing EPOS system", "Works alongside your website and card machines", "Switching from another platform? We handle the migration"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                    <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
                <h3 className="font-heading text-xl font-bold text-brand-black mb-4">Set up online ordering</h3>
                <ContactForm />
              </div>
              <FormReviewCompanion product="online-ordering" industry="hospitality" testId="online-ordering-form-review" />
            </div>
          </div>
        </div>
      </section>

      <LocationCrossLinks />
      <RelatedReviews
        product="online-ordering"
        eyebrow="Customer Stories"
        heading="See what our customers say about our online ordering platform"
        subheading="Real feedback from UK hospitality and retail teams who let customers order online with CanDo."
        testId="online-ordering-related-reviews"
      />
    </div>
  );
}
